import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import VoiceNoteRecorder from "./VoiceNoteRecorder";

import { ReactComponent as RectangleActive } from "../../../../assets/icons/svg/rectangle-active.svg";
import { ReactComponent as SelectionActive } from "../../../../assets/icons/svg/selection-active.svg";
import { ReactComponent as EclipseActive } from "../../../../assets/icons/svg/eclipse-active.svg";
import { ReactComponent as AlertIcon } from "../../../../assets/icons/svg/Alert-icon.svg";
import { ReactComponent as UndoIcon } from "../../../../assets/icons/svg/undo.svg";
import Button from "../../buttons";
import { API_URLS, ReshareApiService } from "../../../../api/urls";

import TextEditor from "../../TextEditor";
import { EnhancedTokenManager } from "../../../../utils/tokenManager";
import {
  compressImage,
  needsCompression,
  getImageSizeMB,
} from "../../../../utils/imageCompression";
import Lottie from "lottie-react";
import DotLoader from "../../../../assets/Animation/Dot Loader.json";
import { DEFAULT_FONT_FAMILY } from "../../../../config/fontConfig";

export interface ScreenshotPreview {
  dataUrl: string;
  area: {
    startX: number;
    startY: number;
    endX: number;
    endY: number;
  };
  actualWidth: number;
  actualHeight: number;
}

export interface ScreenshotSubmission {
  id: string;
  feedback: string;
  annotatedImage: string;
  originalImage: string;
  timestamp: number;
  hasAudio: boolean;
  audioUrl?: string;
  audioBlob?: Blob;
  audioFilename?: string;
  uniqueFeedbackId?: string;
  startRect?: DOMRect | null;
}

interface ScreenshotPreviewProps {
  preview: ScreenshotPreview | null;
  isLoading: boolean;
  onClose: () => void;
  onDownload: () => void;
  onSubmit?: (payload: ScreenshotSubmission) => void;
  uniqueFeedbackId?: string;
  isEditMode?: boolean; // Flag to indicate edit mode
  existingFeedback?: string; // Pre-populate feedback text
  existingAudioUrl?: string; // Pre-populate audio URL
  existingAudioBlob?: Blob; // Pre-populate audio blob
  existingAudioFilename?: string; // Pre-populate audio filename
  isFromViewListFeedback?: boolean;
}

type Tool = "selection" | "rect" | "ellipse";
type Point = { x: number; y: number };
type ShapeBase = { color: string; tool: Tool; lineWidth: number };
type RectShape = ShapeBase & { x: number; y: number; w: number; h: number };
type Shape = RectShape;

type DragMode =
  | "none"
  | "move"
  | "resize-tl"
  | "resize-tr"
  | "resize-bl"
  | "resize-br"
  | "resize-t"
  | "resize-b"
  | "resize-l"
  | "resize-r"
  | "delete";

interface DragState {
  mode: DragMode;
  shapeIndex: number;
  startX: number;
  startY: number;
  originalShape: Shape | null;
}

const COLORS = ["#ff4d4f", "#52c41a", "#1890ff", "#faad14", "#722ed1"];
const TOOLS: Tool[] = ["selection", "rect", "ellipse"];
const BORDER_WIDTHS = [2, 3, 4, 6, 8];

// API Response interfaces
interface UploadMessageResponse {
  status: "uploaded";
  message?: string;
}

interface UploadFileResponse {
  status: "uploaded";
  file_url: string;
}

interface UploadVoiceResponse {
  status: "uploaded";
  voice_url: string;
}

/**
 * Audio conversion utility for MP3 format
 */
class AudioConverter {
  static async convertToMP3(inputBlob: Blob): Promise<Blob> {
    try {
      if (inputBlob.type.includes("mp3") || inputBlob.type.includes("mpeg")) {
        console.log("🎵 Audio is already MP3/MPEG format");
        return inputBlob;
      }

      console.log("🎵 Converting audio to MP3 format...");
      const mp3Blob = new Blob([inputBlob], { type: "audio/mp3" });

      console.log("🎵 Audio conversion to MP3 successful:", {
        originalSize: inputBlob.size,
        convertedSize: mp3Blob.size,
        originalType: inputBlob.type,
        convertedType: mp3Blob.type,
      });

      return mp3Blob;
    } catch (error) {
      console.warn(
        "🎵 MP3 conversion failed, creating MP3-compatible fallback:",
        error,
      );
      return new Blob([inputBlob], { type: "audio/mp3" });
    }
  }
}

/**
 * API Service for Screenshot APIs
 */
class ScreenshotApiService {
  static async uploadMessage(
    message: string,
    feedbackId: string | number,
  ): Promise<UploadMessageResponse> {
    const accessToken = EnhancedTokenManager.getAccessToken();
    if (!accessToken) {
      throw new Error("No access token available");
    }

    const cleanFeedbackId = String(feedbackId).trim();
    const currentUserId = EnhancedTokenManager.getCurrentUserId();
    const workspaceId = EnhancedTokenManager.getWorkspaceIdForCurrentUser();

    console.log("💬 Uploading message:", {
      messageLength: message.length,
      feedbackId: cleanFeedbackId,
      currentUserId,
      workspaceId,
      messagePreview:
        message.substring(0, 100) + (message.length > 100 ? "..." : ""),
    });

    const formData = new FormData();
    formData.append("message", message);
    formData.append("feedback_id", cleanFeedbackId);

    if (workspaceId) {
      formData.append("workspace_id", workspaceId.toString());
    }

    console.log("💬 FormData contents:");
    const entries = Array.from(formData.entries());
    entries.forEach(([key, value]) => {
      console.log(
        `  ${key}: ${
          typeof value === "string"
            ? value.substring(0, 100) + (value.length > 100 ? "..." : "")
            : value
        }`,
      );
    });

    const response = await fetch(API_URLS.UPLOAD_MESSAGE, {
      method: "POST",
      body: formData,
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    console.log("💬 Response status:", response.status);

    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      try {
        const errorText = await response.text();
        console.error("💬 Error response body:", errorText);
        if (errorText) {
          errorMessage = `HTTP ${response.status}: ${errorText}`;
        }
      } catch (e) {
        console.error("💬 Could not read error response:", e);
      }
      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log("✅ Message uploaded successfully:", result);
    return result;
  }

  static async uploadFile(
    file: File | Blob,
    fileName: string,
    feedbackId: string | number,
  ): Promise<UploadFileResponse> {
    const accessToken = EnhancedTokenManager.getAccessToken();
    if (!accessToken) {
      throw new Error("No access token available");
    }

    const cleanFeedbackId = String(feedbackId).trim();
    const currentUserId = EnhancedTokenManager.getCurrentUserId();
    const workspaceId = EnhancedTokenManager.getWorkspaceIdForCurrentUser();

    console.log("🖼️ Uploading file:", {
      fileName,
      feedbackId: cleanFeedbackId,
      fileSize: file.size,
      fileType: file.type,
      currentUserId,
      workspaceId,
    });

    let processedFile: Blob = file;

    if (file.type.startsWith("image/") || fileName.endsWith(".png")) {
      try {
        const img = new Image();
        const imageUrl = URL.createObjectURL(file);

        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = imageUrl;
        });

        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");

        if (ctx) {
          ctx.drawImage(img, 0, 0);

          const pngBlob = await new Promise<Blob>((resolve, reject) => {
            canvas.toBlob(
              (blob) => {
                if (blob) {
                  resolve(blob);
                } else {
                  reject(new Error("Failed to create PNG blob"));
                }
              },
              "image/png",
              1.0,
            );
          });

          processedFile = pngBlob;
          console.log("✅ Image converted to PNG format:", {
            originalSize: file.size,
            pngSize: pngBlob.size,
            originalType: file.type,
            pngType: pngBlob.type,
          });
        }

        URL.revokeObjectURL(imageUrl);
      } catch (conversionError) {
        console.warn(
          "Image conversion failed, using original:",
          conversionError,
        );
        processedFile = new Blob([file], { type: "image/png" });
      }
    }

    const formData = new FormData();
    const ensuredFileName = fileName.endsWith(".png")
      ? fileName
      : `${fileName}.png`;

    const fileObject = new File([processedFile], ensuredFileName, {
      type: "image/png",
      lastModified: Date.now(),
    });

    formData.append("file", fileObject, ensuredFileName);
    formData.append("feedback_id", cleanFeedbackId);
    formData.append("file_type", "screenshot");

    if (workspaceId) {
      formData.append("workspace_id", workspaceId.toString());
    }

    console.log("🖼️ FormData contents:");
    const entries = Array.from(formData.entries());
    entries.forEach(([key, value]) => {
      if (value instanceof File) {
        console.log(
          `  ${key}: File(${value.name}, ${value.size} bytes, ${value.type})`,
        );
      } else {
        console.log(`  ${key}: ${value}`);
      }
    });

    const response = await fetch(API_URLS.UPLOAD_FILE, {
      method: "POST",
      body: formData,
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      mode: "cors",
      credentials: "omit",
    });

    console.log("🖼️ Response status:", response.status);

    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      try {
        const errorText = await response.text();
        console.error("🖼️ Error response body:", errorText);
        if (errorText) {
          errorMessage = `HTTP ${response.status}: ${errorText}`;
        }
      } catch (e) {
        console.error("🖼️ Could not read error response:", e);
      }

      if (response.status === 0) {
        throw new Error(
          "Network error: Cannot reach upload server. Check CORS configuration.",
        );
      }

      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log("✅ File uploaded successfully:", result);
    return result;
  }

  static async uploadVoice(
    audioFile: File | Blob,
    fileName: string,
    feedbackId: string | number,
  ): Promise<UploadVoiceResponse> {
    const accessToken = EnhancedTokenManager.getAccessToken();
    if (!accessToken) {
      throw new Error("No access token available");
    }

    const cleanFeedbackId = String(feedbackId).trim();
    const currentUserId = EnhancedTokenManager.getCurrentUserId();
    const workspaceId = EnhancedTokenManager.getWorkspaceIdForCurrentUser();

    console.log("🎵 Uploading voice note:", {
      fileName,
      feedbackId: cleanFeedbackId,
      fileSize: audioFile.size,
      fileType: audioFile.type,
      currentUserId,
      workspaceId,
    });

    let processedAudioFile = audioFile;
    let processedFileName = fileName;

    console.log("🎵 Converting audio to MP3 for API compatibility...");
    try {
      processedAudioFile = await AudioConverter.convertToMP3(audioFile);
      processedFileName = fileName.replace(/\.(wav|webm|ogg|mp4)$/, ".mp3");
      console.log("🎵 Audio conversion successful:", {
        originalSize: audioFile.size,
        convertedSize: processedAudioFile.size,
        newFileName: processedFileName,
      });
    } catch (conversionError) {
      console.warn(
        "🎵 Audio conversion failed, using fallback:",
        conversionError,
      );
      processedAudioFile = new Blob([audioFile], { type: "audio/mp3" });
      processedFileName = fileName.replace(/\.(wav|webm|ogg|mp4)$/, ".mp3");
    }

    const formData = new FormData();

    if (
      processedAudioFile instanceof Blob &&
      !(processedAudioFile instanceof File)
    ) {
      const fileObject = new File([processedAudioFile], processedFileName, {
        type: processedAudioFile.type || "audio/mp3",
        lastModified: Date.now(),
      });
      formData.append("file", fileObject);
    } else {
      formData.append("file", processedAudioFile, processedFileName);
    }
    formData.append("feedback_id", cleanFeedbackId);
    formData.append("file_type", "audio");

    if (workspaceId) {
      formData.append("workspace_id", workspaceId.toString());
    }

    console.log("🎵 FormData contents:");
    const entries = Array.from(formData.entries());
    entries.forEach(([key, value]) => {
      if (value instanceof File) {
        console.log(
          `  ${key}: File(${value.name}, ${value.size} bytes, ${value.type})`,
        );
      } else {
        console.log(`  ${key}: ${value}`);
      }
    });

    const response = await fetch(API_URLS.UPLOAD_FILE_VOICE, {
      method: "POST",
      body: formData,
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    console.log("🎵 Response status:", response.status);

    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      try {
        const errorText = await response.text();
        console.error("🎵 Error response body:", errorText);
        if (errorText) {
          errorMessage = `HTTP ${response.status}: ${errorText}`;
        }
      } catch (e) {
        console.error("🎵 Could not read error response:", e);
      }
      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log("✅ Voice note uploaded successfully:", result);
    return result;
  }
}

const ScreenshotPreviewComponent: React.FC<ScreenshotPreviewProps> = ({
  preview,
  isLoading,
  onClose,
  onDownload,
  onSubmit,
  uniqueFeedbackId,
  isEditMode = false,
  existingFeedback = "",
  existingAudioUrl,
  existingAudioBlob,
  existingAudioFilename,
  isFromViewListFeedback = false,
}) => {
  const previewContainerRef = useRef<HTMLDivElement | null>(null);
  const [feedback, setFeedback] = useState(existingFeedback || "");
  const [tool, setTool] = useState<Tool | null>("rect");
  const [persistentTool, setPersistentTool] = useState<Tool>("rect");
  const [color, setColor] = useState<string>(COLORS[0]);
  const [borderWidth, setBorderWidth] = useState<number>(BORDER_WIDTHS[0]);
  const [selectedBorderWidth, setSelectedBorderWidth] = useState<number>(
    BORDER_WIDTHS[0],
  );
  const [shapes, setShapes] = useState<Shape[]>([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [draftShape, setDraftShape] = useState<Shape | null>(null);
  const [showColorPalette, setShowColorPalette] = useState(true);
  const [showWidthSelector, setShowWidthSelector] = useState(false);

  const [dragState, setDragState] = useState<DragState>({
    mode: "none",
    shapeIndex: -1,
    startX: 0,
    startY: 0,
    originalShape: null,
  });
  const [selectedShape, setSelectedShape] = useState<number>(-1);
  const [hoveredHandle, setHoveredHandle] = useState<DragMode>("none");
  const [hoveredShape, setHoveredShape] = useState<number>(-1);

  const [hasAudioRecording, setHasAudioRecording] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | undefined>(undefined);
  const [audioDownloadUrl, setAudioDownloadUrl] = useState<string | undefined>(
    undefined,
  );
  const [audioFilename, setAudioFilename] = useState<string | undefined>(
    undefined,
  );
  const [audioBlob, setAudioBlob] = useState<Blob | undefined>(undefined);
  const [isAudioInitialized, setIsAudioInitialized] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string>("");
  const [voiceRecorderKey, setVoiceRecorderKey] = useState(0);

  const [animationPhase, setAnimationPhase] = useState<
    "opening" | "open" | "closing" | "closed"
  >("closed");
  const [isAnimating, setIsAnimating] = useState(false);

  const imgRef = useRef<HTMLImageElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const paletteRef = useRef<HTMLDivElement | null>(null);
  const hasInitialized = useRef(false);
  const [displaySize, setDisplaySize] = useState({ w: 0, h: 0 });
  const [naturalSize, setNaturalSize] = useState({ w: 0, h: 0 });

  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const [isTextEditorFocused, setIsTextEditorFocused] = useState(false);
  const [feedbackError, setFeedbackError] = useState<string>("");
  const [hasInteractedWithEditor, setHasInteractedWithEditor] = useState(false);

  const hasActualTextContent = (htmlContent: string): boolean => {
    // Remove HTML tags and check if there's actual text
    const textOnly = htmlContent.replace(/<[^>]*>/g, "").trim();
    return textOnly.length > 0;
  };

  // Show button as enabled when text editor has been interacted with OR has content
  const shouldEnableButton =
    hasInteractedWithEditor ||
    (feedback.trim().length > 0 && hasActualTextContent(feedback));

  // Actually has content for submission validation
  const hasContent =
    feedback.trim().length > 0 && hasActualTextContent(feedback);

  useEffect(() => {
    const currentUserId = EnhancedTokenManager.getCurrentUserId();
    const workspaceId = EnhancedTokenManager.getWorkspaceIdForCurrentUser();
    const hasCompleteSetup = EnhancedTokenManager.hasCompleteSetup();

    console.log("📸 ScreenshotPreview - Component state changed:", {
      uniqueFeedbackId,
      type: typeof uniqueFeedbackId,
      isValid: uniqueFeedbackId && String(uniqueFeedbackId).trim() !== "",
      currentUserId,
      workspaceId,
      hasCompleteSetup,
    });
  }, [uniqueFeedbackId]);

  const resizeCanvas = () => {
    const img = imgRef.current;
    const canvas = canvasRef.current;
    if (!img || !canvas) {
      console.log("🎨 Canvas resize skipped: missing refs");
      return;
    }

    const rect = img.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      console.log("🎨 Canvas resize skipped: zero dimensions");
      return;
    }

    const dpr = window.devicePixelRatio || 1;

    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;

    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.scale(dpr, dpr);
    }

    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;

    setDisplaySize({ w: rect.width, h: rect.height });

    console.log("🎨 Canvas resized:", {
      canvasWidth: canvas.width,
      canvasHeight: canvas.height,
      displayWidth: rect.width,
      displayHeight: rect.height,
      dpr,
    });

    draw();
  };

  useEffect(() => {
    const onResize = () => {
      clearTimeout((window as any).canvasResizeTimeout);
      (window as any).canvasResizeTimeout = setTimeout(resizeCanvas, 100);
    };

    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      clearTimeout((window as any).canvasResizeTimeout);
    };
  }, []);

  useEffect(() => {
    draw();
  }, [
    shapes,
    draftShape,
    displaySize,
    selectedShape,
    hoveredHandle,
    dragState,
    hoveredShape,
  ]);

  useEffect(() => {
    // Dispatch event when preview opens
    if (preview) {
      console.log("📸 ScreenshotPreview: Dispatching preview-opened event");
      window.dispatchEvent(new CustomEvent("preview-opened"));
    }

    // Cleanup: dispatch event when preview closes
    return () => {
      console.log("📸 ScreenshotPreview: Dispatching preview-closed event");
      window.dispatchEvent(new CustomEvent("preview-closed"));
    };
  }, [preview]);

  useEffect(() => {
    if (preview && !hasInitialized.current) {
      console.log(
        "📸 ScreenshotPreview - New preview loaded, starting opening animation",
      );
      setAnimationPhase("opening");
      setIsAnimating(true);

      if (isEditMode && existingFeedback) {
        // In edit mode with existing feedback - only reset drawing-related state
        setShapes([]);
        setDraftShape(null);
        setIsDrawing(false);
        setTool("rect");
        setShowColorPalette(true);
        setShowWidthSelector(false);
        setSubmitError("");
        setFeedbackError(""); // ✅ NEW: Clear error in edit mode
        // Keep feedback and audio in edit mode
      } else {
        // New screenshot OR edit mode without existing feedback - reset everything
        setShapes([]);
        setDraftShape(null);
        setFeedback("");
        setIsDrawing(false);
        setTool("rect");
        setShowColorPalette(true);
        setShowWidthSelector(false);
        setSubmitError("");
        setFeedbackError(""); // ✅ NEW: Clear error for new screenshot
        setHasInteractedWithEditor(false); // ✅ NEW: Reset interaction flag
        setHasAudioRecording(false);
        setAudioUrl(undefined);
        setAudioDownloadUrl(undefined);
        setAudioFilename(undefined);
        setAudioBlob(undefined);
        setVoiceRecorderKey((prev) => prev + 1);
      }

      setDragState({
        mode: "none",
        shapeIndex: -1,
        startX: 0,
        startY: 0,
        originalShape: null,
      });
      setSelectedShape(-1);
      setHoveredHandle("none");
      setHoveredShape(-1);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setAnimationPhase("open");
          setTimeout(() => {
            setIsAnimating(false);
          }, 500);
        });
      });

      hasInitialized.current = true;
    }
  }, [preview?.dataUrl, isEditMode]);

  // Reset initialization flag when component unmounts or preview changes to null
  useEffect(() => {
    if (!preview) {
      hasInitialized.current = false;
      // ✅ ADDED: Also reset feedback state when preview is closed
      if (!isEditMode) {
        setFeedback("");
      }
    }
  }, [preview, isEditMode]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        paletteRef.current &&
        !paletteRef.current.contains(event.target as Node)
      ) {
        setShowColorPalette(false);
        setShowWidthSelector(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (audioUrl && audioUrl.startsWith("blob:")) {
        URL.revokeObjectURL(audioUrl);
      }
      if (
        audioDownloadUrl &&
        audioDownloadUrl.startsWith("blob:") &&
        audioDownloadUrl !== audioUrl
      ) {
        URL.revokeObjectURL(audioDownloadUrl);
      }
    };
  }, [audioUrl, audioDownloadUrl]);
  useEffect(() => {
    if (preview?.dataUrl && preview.dataUrl !== "") {
      console.log("✅ Image data is now available:", {
        length: preview.dataUrl.length,
        type: preview.dataUrl.substring(0, 30),
      });
      setIsLoadingImage(false);
    }
  }, [preview?.dataUrl]);
  // Initialize edit mode data
  useEffect(() => {
    if (isEditMode && !isAudioInitialized) {
      console.log("📸 ScreenshotPreview - Initializing edit mode:", {
        existingFeedback: existingFeedback?.substring(0, 50),
        hasExistingAudio: !!existingAudioUrl,
        hasExistingBlob: !!existingAudioBlob,
        existingAudioFilename,
      });

      // Set feedback text
      if (existingFeedback) {
        setFeedback(existingFeedback);
        setHasInteractedWithEditor(true); // ✅ NEW: Set flag in edit mode
      }

      // ✅ NEW: Check if we're still waiting for the image to load
      if (!preview?.dataUrl || preview.dataUrl === "") {
        console.log("⏳ Waiting for image data to load...");
        setIsLoadingImage(true);
      } else {
        setIsLoadingImage(false);
      }

      // Set audio data if available
      const hasAudio = !!(existingAudioUrl || existingAudioBlob);

      if (hasAudio) {
        setHasAudioRecording(true);
        setAudioUrl(existingAudioUrl);
        setAudioDownloadUrl(existingAudioUrl);
        setAudioFilename(existingAudioFilename);
        setAudioBlob(existingAudioBlob);
        setIsAudioInitialized(true);

        console.log("✅ Edit mode audio initialized:", {
          audioUrl: existingAudioUrl?.substring(0, 50),
          filename: existingAudioFilename,
          blobSize: existingAudioBlob?.size,
          hasAudio,
        });
      } else {
        setHasAudioRecording(false);
        setIsAudioInitialized(true);
        console.log("📸 Edit mode: No audio to initialize");
      }
    }
  }, [
    isEditMode,
    existingFeedback,
    existingAudioUrl,
    existingAudioBlob,
    existingAudioFilename,
    isAudioInitialized,
    preview?.dataUrl, // ✅ Add this dependency
  ]);

  useEffect(() => {
    console.log("📸 VoiceNoteRecorder rendering with props:", {
      isEditMode,
      hasExistingAudioUrl: !!existingAudioUrl,
      hasExistingAudioBlob: !!existingAudioBlob,
      willShowRecorded: isEditMode && (existingAudioUrl || existingAudioBlob),
      voiceRecorderKey,
    });
  }, [isEditMode, existingAudioUrl, existingAudioBlob, voiceRecorderKey]);

  const getRelativePos = (e: React.MouseEvent<HTMLCanvasElement>): Point => {
    const rect = canvasRef.current!.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  };

  const hexToRgba = (hex: string, opacity: number): string => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

  const isPointInShape = (x: number, y: number, shape: Shape): boolean => {
    const minX = Math.min(shape.x, shape.x + shape.w);
    const maxX = Math.max(shape.x, shape.x + shape.w);
    const minY = Math.min(shape.y, shape.y + shape.h);
    const maxY = Math.max(shape.y, shape.y + shape.h);

    return x >= minX && x <= maxX && y >= minY && y <= maxY;
  };

  const getResizeHandle = (x: number, y: number, shape: Shape): DragMode => {
    const handleSize = 16; // Increased for better bar detection
    const minX = Math.min(shape.x, shape.x + shape.w);
    const maxX = Math.max(shape.x, shape.x + shape.w);
    const minY = Math.min(shape.y, shape.y + shape.h);
    const maxY = Math.max(shape.y, shape.y + shape.h);

    // ✅ NEW: Check delete button first (right-top corner)
    const deleteSize = 16;
    const deleteX = maxX + deleteSize / 2; // ✅ Changed: positioned to the right
    const deleteY = minY - deleteSize / 2;
    const distToDelete = Math.sqrt(
      Math.pow(x - deleteX, 2) + Math.pow(y - deleteY, 2),
    );
    if (distToDelete <= deleteSize / 2) {
      return "delete" as DragMode;
    }

    // Only edge handles (no corners) - with larger detection area for bars
    if (Math.abs(y - minY) <= handleSize && x >= minX && x <= maxX)
      return "resize-t";
    if (Math.abs(y - maxY) <= handleSize && x >= minX && x <= maxX)
      return "resize-b";
    if (Math.abs(x - minX) <= handleSize && y >= minY && y <= maxY)
      return "resize-l";
    if (Math.abs(x - maxX) <= handleSize && y >= minY && y <= maxY)
      return "resize-r";

    return "none";
  };

  const drawResizeHandles = (
    ctx: CanvasRenderingContext2D,
    shape: Shape,
    shapeColor: string,
  ) => {
    const minX = Math.min(shape.x, shape.x + shape.w);
    const maxX = Math.max(shape.x, shape.x + shape.w);
    const minY = Math.min(shape.y, shape.y + shape.h);
    const maxY = Math.max(shape.y, shape.y + shape.h);
    const midX = (minX + maxX) / 2;
    const midY = (minY + maxY) / 2;

    // Bar style settings
    const barThickness = 6;
    const barLength = 28;
    const borderRadius = 3; // Rounded corners

    // Only middle edge handles as vertical/horizontal bars
    const handles = [
      { x: midX, y: minY, type: "resize-t", isVertical: false }, // Top - horizontal bar
      { x: midX, y: maxY, type: "resize-b", isVertical: false }, // Bottom - horizontal bar
      { x: minX, y: midY, type: "resize-l", isVertical: true }, // Left - vertical bar
      { x: maxX, y: midY, type: "resize-r", isVertical: true }, // Right - vertical bar
    ];

    handles.forEach((handle) => {
      const isHandleHovered = hoveredHandle === handle.type;

      // Use shape's color instead of blue
      ctx.fillStyle = isHandleHovered ? shapeColor : "#ffffff";
      ctx.strokeStyle = "#D8E1FF";
      ctx.lineWidth = 1;

      ctx.beginPath();
      if (handle.isVertical) {
        // Vertical bar with rounded corners
        const x = handle.x - barThickness / 2;
        const y = handle.y - barLength / 2;
        ctx.roundRect(x, y, barThickness, barLength, borderRadius);
      } else {
        // Horizontal bar with rounded corners
        const x = handle.x - barLength / 2;
        const y = handle.y - barThickness / 2;
        ctx.roundRect(x, y, barLength, barThickness, borderRadius);
      }
      ctx.fill();
      ctx.stroke();
    });

    // ✅ NEW: Draw delete button at right-top corner
    const deleteSize = 16;
    const deleteX = maxX + deleteSize / 2; // ✅ Changed: positioned to the right
    const deleteY = minY - deleteSize / 2;

    // Delete button background (red circle)
    ctx.fillStyle = hoveredHandle === "delete" ? "#ef4444" : "#ff4d4f";
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(deleteX, deleteY, deleteSize / 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    // Delete icon (X)
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    const iconSize = 6;
    ctx.beginPath();
    ctx.moveTo(deleteX - iconSize / 2, deleteY - iconSize / 2);
    ctx.lineTo(deleteX + iconSize / 2, deleteY + iconSize / 2);
    ctx.moveTo(deleteX + iconSize / 2, deleteY - iconSize / 2);
    ctx.lineTo(deleteX - iconSize / 2, deleteY + iconSize / 2);
    ctx.stroke();
  };

  const draw = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d")!;
    const dpr = window.devicePixelRatio || 1;

    ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);

    const drawShape = (s: Shape, index: number) => {
      const isHovered = index === hoveredShape; // Changed from isSelected

      if (s.tool === "selection") {
        ctx.strokeStyle = s.color;
        ctx.fillStyle = hexToRgba(s.color, 0.1);
        ctx.lineWidth = Math.max(1, (s.lineWidth || 2) / dpr);
        ctx.setLineDash([8, 4]);
        ctx.fillRect(s.x, s.y, s.w, s.h);
        ctx.strokeRect(s.x, s.y, s.w, s.h);
        ctx.setLineDash([]);
        // Removed blue selection border
      } else {
        ctx.strokeStyle = s.color;
        ctx.lineWidth = Math.max(1, (s.lineWidth || 2) / dpr);
        if (s.tool === "rect") {
          ctx.strokeRect(s.x, s.y, s.w, s.h);
        } else if (s.tool === "ellipse") {
          ctx.beginPath();
          ctx.ellipse(
            s.x + s.w / 2,
            s.y + s.h / 2,
            Math.abs(s.w / 2),
            Math.abs(s.h / 2),
            0,
            0,
            Math.PI * 2,
          );
          ctx.stroke();
        }
        // Removed blue selection border
      }

      // Show handles on hover instead of selection
      if (isHovered) {
        drawResizeHandles(ctx, s, s.color); // Pass shape's color
      }
    };

    shapes.forEach((shape, index) => drawShape(shape, index));

    if (draftShape) drawShape(draftShape, -1);
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (e.button !== 0) return;

    const pos = getRelativePos(e);

    // Check if clicking on a hovered shape's resize handle or inside shape
    if (hoveredShape >= 0 && hoveredShape < shapes.length) {
      const shape = shapes[hoveredShape];
      const handle = getResizeHandle(pos.x, pos.y, shape);

      // ✅ NEW: Handle delete button click
      if (handle === "delete") {
        setShapes((prev) => prev.filter((_, index) => index !== hoveredShape));
        setSelectedShape(-1);
        setHoveredShape(-1);
        setHoveredHandle("none");
        return;
      }

      if (handle !== "none") {
        setDragState({
          mode: handle,
          shapeIndex: hoveredShape,
          startX: pos.x,
          startY: pos.y,
          originalShape: { ...shape },
        });
        setSelectedShape(hoveredShape);
        return;
      }

      if (isPointInShape(pos.x, pos.y, shape)) {
        setDragState({
          mode: "move",
          shapeIndex: hoveredShape,
          startX: pos.x,
          startY: pos.y,
          originalShape: { ...shape },
        });
        setSelectedShape(hoveredShape);
        return;
      }
    }

    // Drawing new highlight with persistent tool
    console.log("🎨 Mouse down:", { tool: persistentTool, button: e.button });
    setSelectedShape(-1);
    setHoveredShape(-1);
    setIsDrawing(true);
    const start = getRelativePos(e);

    console.log("🎨 Starting shape:", {
      tool: persistentTool,
      startPos: start,
    });

    setDraftShape({
      tool: persistentTool,
      color,
      lineWidth: selectedBorderWidth,
      x: start.x,
      y: start.y,
      w: 0,
      h: 0,
    });
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const pos = getRelativePos(e);

    if (dragState.mode !== "none" && dragState.originalShape) {
      e.preventDefault();
      const dx = pos.x - dragState.startX;
      const dy = pos.y - dragState.startY;
      const original = dragState.originalShape;

      let updatedShape = { ...original };

      if (dragState.mode === "move") {
        updatedShape.x = original.x + dx;
        updatedShape.y = original.y + dy;
      } else if (dragState.mode.startsWith("resize-")) {
        const minX = Math.min(original.x, original.x + original.w);
        const maxX = Math.max(original.x, original.x + original.w);
        const minY = Math.min(original.y, original.y + original.h);
        const maxY = Math.max(original.y, original.y + original.h);

        switch (dragState.mode) {
          case "resize-tl":
            updatedShape.x = minX + dx;
            updatedShape.y = minY + dy;
            updatedShape.w = maxX - minX - dx;
            updatedShape.h = maxY - minY - dy;
            break;
          case "resize-tr":
            updatedShape.x = minX;
            updatedShape.y = minY + dy;
            updatedShape.w = maxX - minX + dx;
            updatedShape.h = maxY - minY - dy;
            break;
          case "resize-bl":
            updatedShape.x = minX + dx;
            updatedShape.y = minY;
            updatedShape.w = maxX - minX - dx;
            updatedShape.h = maxY - minY + dy;
            break;
          case "resize-br":
            updatedShape.x = minX;
            updatedShape.y = minY;
            updatedShape.w = maxX - minX + dx;
            updatedShape.h = maxY - minY + dy;
            break;
          case "resize-t":
            updatedShape.x = minX;
            updatedShape.y = minY + dy;
            updatedShape.w = maxX - minX;
            updatedShape.h = maxY - minY - dy;
            break;
          case "resize-b":
            updatedShape.x = minX;
            updatedShape.y = minY;
            updatedShape.w = maxX - minX;
            updatedShape.h = maxY - minY + dy;
            break;
          case "resize-l":
            updatedShape.x = minX + dx;
            updatedShape.y = minY;
            updatedShape.w = maxX - minX - dx;
            updatedShape.h = maxY - minY;
            break;
          case "resize-r":
            updatedShape.x = minX;
            updatedShape.y = minY;
            updatedShape.w = maxX - minX + dx;
            updatedShape.h = maxY - minY;
            break;
        }
      }

      setShapes((prev) => {
        const newShapes = [...prev];
        newShapes[dragState.shapeIndex] = updatedShape;
        return newShapes;
      });
      return;
    }

    if (isDrawing && draftShape) {
      e.preventDefault();
      setDraftShape({
        ...draftShape,
        w: pos.x - draftShape.x,
        h: pos.y - draftShape.y,
      });
      return;
    }

    // Check for hover over shapes and their handles
    if (!isDrawing && dragState.mode === "none") {
      let foundHoveredShape = -1;
      let foundHandle: DragMode = "none";

      // Check from top to bottom (reverse order)
      for (let i = shapes.length - 1; i >= 0; i--) {
        const shape = shapes[i];
        const handle = getResizeHandle(pos.x, pos.y, shape);
        if (handle !== "none") {
          foundHoveredShape = i;
          foundHandle = handle;
          break;
        }
        if (isPointInShape(pos.x, pos.y, shape)) {
          foundHoveredShape = i;
          foundHandle = "move";
          break;
        }
      }

      setHoveredShape(foundHoveredShape);
      setHoveredHandle(foundHandle);
    } else {
      setHoveredShape(-1);
      setHoveredHandle("none");
    }
  };

  const handleMouseUp = () => {
    if (dragState.mode !== "none") {
      setDragState({
        mode: "none",
        shapeIndex: -1,
        startX: 0,
        startY: 0,
        originalShape: null,
      });
      return;
    }

    if (!draftShape) return;
    setIsDrawing(false);

    // Add new shape and keep the persistent tool selected
    setShapes((prev) => [...prev, draftShape]);
    setSelectedShape(shapes.length);
    setDraftShape(null);
    // Tool remains as persistentTool, no need to change it
  };

  const exportAnnotatedImage = async (): Promise<string> => {
    return new Promise(async (resolve, reject) => {
      try {
        const imageUrl = preview?.dataUrl || "";

        console.log("🎨 Starting exportAnnotatedImage:", {
          imageUrl: imageUrl.substring(0, 50),
          shapesCount: shapes.length,
          displaySize,
          naturalSize,
          imageType: imageUrl.startsWith("data:")
            ? "data-url"
            : imageUrl.startsWith("blob:")
              ? "blob-url"
              : imageUrl.startsWith("http")
                ? "remote-url"
                : "unknown",
          isEditMode, // Log edit mode status
        });

        // ✅ CRITICAL FIX 1: Validate input immediately
        if (!imageUrl) {
          console.error("❌ No image URL provided");
          reject(new Error("No image to export"));
          return;
        }

        // ✅ CRITICAL FIX 2: Handle all image source types with robust conversion
        let finalImageUrl = imageUrl;

        // Convert blob URLs to data URLs for better compatibility
        if (imageUrl.startsWith("blob:")) {
          try {
            console.log("🔄 Converting blob URL to data URL...");

            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 10000);

            const response = await fetch(imageUrl, {
              signal: controller.signal,
            });
            clearTimeout(timeoutId);

            if (!response.ok) {
              throw new Error(
                `Fetch failed: ${response.status} ${response.statusText}`,
              );
            }

            const blob = await response.blob();

            if (blob.size === 0) {
              throw new Error("Blob is empty (0 bytes)");
            }

            console.log("✅ Blob fetched:", {
              size: blob.size,
              type: blob.type,
            });

            finalImageUrl = await new Promise<string>(
              (resolveRead, rejectRead) => {
                const reader = new FileReader();

                reader.onloadend = () => {
                  const result = reader.result as string;
                  if (result && result.startsWith("data:")) {
                    console.log("✅ Blob converted to data URL");
                    resolveRead(result);
                  } else {
                    rejectRead(
                      new Error("Invalid data URL produced from blob"),
                    );
                  }
                };

                reader.onerror = () =>
                  rejectRead(new Error("FileReader failed to read blob"));
                reader.readAsDataURL(blob);
              },
            );
          } catch (conversionError) {
            console.error("❌ Blob conversion failed:", conversionError);
            reject(
              new Error(
                `Failed to convert blob URL: ${
                  conversionError instanceof Error
                    ? conversionError.message
                    : "Unknown error"
                }`,
              ),
            );
            return;
          }
        }

        // ✅ NEW: Handle remote HTTP/HTTPS URLs (from API in edit mode)
        // SOLUTION: Use img.crossOrigin to bypass CORS (like recordings do)
        if (imageUrl.startsWith("http://") || imageUrl.startsWith("https://")) {
          try {
            console.log("🔄 Fetching remote URL with no-cache headers...");

            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 10000);

            // ✅ Fetch with explicit cache bypass
            const response = await fetch(imageUrl, {
              mode: "cors",
              cache: "no-cache", // ✅ This bypasses cache like "Disable cache" does
              headers: {
                "Cache-Control": "no-cache, no-store, must-revalidate",
                Pragma: "no-cache",
              },
              signal: controller.signal,
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
              throw new Error(`Fetch failed: ${response.status}`);
            }

            const blob = await response.blob();

            if (blob.size === 0) {
              throw new Error("Blob is empty");
            }

            console.log("✅ Remote image fetched with no-cache:", {
              size: blob.size,
              type: blob.type,
            });

            // Convert blob to data URL
            finalImageUrl = await new Promise<string>(
              (resolveRead, rejectRead) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                  const result = reader.result as string;
                  if (result && result.startsWith("data:")) {
                    console.log("✅ Converted to data URL");
                    resolveRead(result);
                  } else {
                    rejectRead(new Error("Invalid data URL"));
                  }
                };
                reader.onerror = () =>
                  rejectRead(new Error("FileReader failed"));
                reader.readAsDataURL(blob);
              },
            );
          } catch (conversionError) {
            console.error("❌ Remote URL fetch failed:", conversionError);

            // Fallback: use original URL with cache buster
            const separator = imageUrl.includes("?") ? "&" : "?";
            finalImageUrl = `${imageUrl}${separator}_t=${Date.now()}`;
            console.log("⚠️ Using fallback with cache buster");
          }
        }

        // ✅ CRITICAL FIX 3: Validate data URL format (skip for remote URLs)
        if (
          !finalImageUrl.startsWith("data:image/") &&
          !finalImageUrl.startsWith("http://") &&
          !finalImageUrl.startsWith("https://")
        ) {
          console.error(
            "❌ Invalid image format:",
            finalImageUrl.substring(0, 50),
          );
          reject(
            new Error("Invalid image format - must be data URL or remote URL"),
          );
          return;
        }

        // Rest of the function remains the same...
        // [Previous exportAnnotatedImage code continues here]

        const img = new Image();

        // ✅ MUST set crossOrigin BEFORE setting src
        if (
          finalImageUrl.startsWith("http://") ||
          finalImageUrl.startsWith("https://")
        ) {
          img.crossOrigin = "anonymous";
          console.log(
            "✅ Set img.crossOrigin = 'anonymous' BEFORE setting src",
          );
        }

        try {
          await Promise.race([
            new Promise<void>((resolveLoad, rejectLoad) => {
              img.onload = () => {
                console.log("✅ Image loaded:", {
                  naturalWidth: img.naturalWidth,
                  naturalHeight: img.naturalHeight,
                });

                if (img.naturalWidth === 0 || img.naturalHeight === 0) {
                  rejectLoad(new Error("Image has zero dimensions after load"));
                  return;
                }

                resolveLoad();
              };

              img.onerror = (error) => {
                console.error("❌ Image load failed:", error);
                rejectLoad(
                  new Error("Failed to load image - CORS or network issue"),
                );
              };

              // ✅ Set src AFTER crossOrigin is configured
              img.src = finalImageUrl;
            }),
            new Promise((_, rejectTimeout) =>
              setTimeout(
                () =>
                  rejectTimeout(new Error("Image load timeout (15 seconds)")),
                15000,
              ),
            ),
          ]);
        } catch (loadError) {
          console.error("❌ Image loading error:", loadError);
          reject(
            new Error(
              `Image loading failed: ${
                loadError instanceof Error ? loadError.message : "Unknown error"
              }`,
            ),
          );
          return;
        }
        if (img.naturalWidth === 0 || img.naturalHeight === 0) {
          console.error("❌ Image has zero dimensions");
          reject(new Error("Invalid image dimensions (0x0)"));
          return;
        }

        console.log("✅ Image ready for canvas:", {
          width: img.naturalWidth,
          height: img.naturalHeight,
        });

        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;

        const ctx = canvas.getContext("2d", {
          alpha: true,
          willReadFrequently: false,
          desynchronized: false,
        });

        if (!ctx) {
          console.error("❌ Failed to get canvas context");
          reject(new Error("Failed to create canvas context"));
          return;
        }

        console.log("✅ Canvas created:", {
          width: canvas.width,
          height: canvas.height,
        });

        try {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0);

          console.log("✅ Base image drawn to canvas");
        } catch (drawError) {
          console.error("❌ Failed to draw image:", drawError);
          reject(
            new Error(
              `Canvas drawing failed: ${
                drawError instanceof Error ? drawError.message : "Unknown error"
              }`,
            ),
          );
          return;
        }

        const sx = naturalSize.w > 0 ? naturalSize.w / displaySize.w : 1;
        const sy = naturalSize.h > 0 ? naturalSize.h / displaySize.h : 1;

        console.log("🎨 Drawing annotations with scale:", {
          shapesCount: shapes.length,
          scaleX: sx.toFixed(2),
          scaleY: sy.toFixed(2),
          naturalSize,
          displaySize,
        });

        let shapesDrawnSuccessfully = 0;

        shapes.forEach((s, index) => {
          try {
            ctx.save();

            if (s.tool === "selection") {
              ctx.strokeStyle = s.color;
              ctx.fillStyle = hexToRgba(s.color, 0.1);
              ctx.lineWidth = (s.lineWidth || 2) * Math.min(sx, sy);
              ctx.setLineDash([16 * sx, 8 * sx]);
              ctx.fillRect(s.x * sx, s.y * sy, s.w * sx, s.h * sy);
              ctx.strokeRect(s.x * sx, s.y * sy, s.w * sx, s.h * sy);
              ctx.setLineDash([]);
            } else {
              ctx.strokeStyle = s.color;
              ctx.lineWidth = (s.lineWidth || 2) * Math.min(sx, sy);

              if (s.tool === "rect") {
                ctx.strokeRect(s.x * sx, s.y * sy, s.w * sx, s.h * sy);
              } else if (s.tool === "ellipse") {
                ctx.beginPath();
                ctx.ellipse(
                  (s.x + s.w / 2) * sx,
                  (s.y + s.h / 2) * sy,
                  Math.abs((s.w / 2) * sx),
                  Math.abs((s.h / 2) * sy),
                  0,
                  0,
                  Math.PI * 2,
                );
                ctx.stroke();
              }
            }

            ctx.restore();
            shapesDrawnSuccessfully++;
          } catch (shapeError) {
            console.warn(`⚠️ Failed to draw shape ${index}:`, shapeError);
          }
        });

        console.log(
          `✅ Shapes drawn: ${shapesDrawnSuccessfully}/${shapes.length}`,
        );

        let result: string | null = null;

        try {
          let dataUrl = canvas.toDataURL("image/png", 1.0);

          if (!dataUrl || !dataUrl.startsWith("data:image/png")) {
            throw new Error("Invalid PNG output");
          }

          if (dataUrl.length < 1000) {
            throw new Error(
              `PNG export produced suspiciously small file (${dataUrl.length} bytes)`,
            );
          }

          const testCanvas = document.createElement("canvas");
          testCanvas.width = 1;
          testCanvas.height = 1;
          const emptyDataUrl = testCanvas.toDataURL("image/png");

          if (dataUrl === emptyDataUrl) {
            throw new Error("PNG export produced empty canvas");
          }

          if (needsCompression(dataUrl, 0.8)) {
            const originalSizeMB = getImageSizeMB(dataUrl);
            console.log(
              `⚠️ Screenshot is large (${originalSizeMB.toFixed(
                2,
              )}MB), compressing...`,
            );

            try {
              dataUrl = await compressImage(dataUrl, {
                maxSizeMB: 0.8,
                initialQuality: 0.85,
                maxDimension: 1920,
                format: "auto",
              });

              console.log("✅ Compression complete:", {
                finalSize: getImageSizeMB(dataUrl).toFixed(2) + "MB",
              });
            } catch (compressionError) {
              console.warn(
                "⚠️ Compression failed, using original:",
                compressionError,
              );
            }
          }

          console.log("✅ PNG export successful:", {
            resultLength: dataUrl.length,
            sizeInMB: getImageSizeMB(dataUrl).toFixed(2) + "MB",
            hasShapes: shapes.length > 0,
          });

          resolve(dataUrl);
          return;
        } catch (pngError) {
          console.warn("⚠️ PNG export failed, trying JPEG fallback:", pngError);

          try {
            const jpegCanvas = document.createElement("canvas");
            jpegCanvas.width = canvas.width;
            jpegCanvas.height = canvas.height;
            const jpegCtx = jpegCanvas.getContext("2d");

            if (!jpegCtx) {
              throw new Error("Failed to get JPEG canvas context");
            }

            jpegCtx.fillStyle = "#ffffff";
            jpegCtx.fillRect(0, 0, jpegCanvas.width, jpegCanvas.height);
            jpegCtx.drawImage(canvas, 0, 0);

            result = jpegCanvas.toDataURL("image/jpeg", 0.95);

            if (!result || !result.startsWith("data:image/jpeg")) {
              throw new Error("JPEG export produced invalid data URL");
            }

            if (result.length < 1000) {
              throw new Error(
                `JPEG export produced suspiciously small file (${result.length} bytes)`,
              );
            }

            console.log("✅ JPEG fallback successful:", {
              resultLength: result.length,
            });

            resolve(result);
            return;
          } catch (jpegError) {
            console.error("❌ Both PNG and JPEG export failed");
            console.error("PNG error:", pngError);
            console.error("JPEG error:", jpegError);

            reject(
              new Error(
                `Canvas export failed. PNG: ${
                  pngError instanceof Error ? pngError.message : "Unknown error"
                }. ` +
                  `JPEG: ${
                    jpegError instanceof Error
                      ? jpegError.message
                      : "Unknown error"
                  }`,
              ),
            );
            return;
          }
        }
      } catch (error) {
        console.error("❌ Critical error in exportAnnotatedImage:", error);
        reject(error instanceof Error ? error : new Error(String(error)));
      }
    });
  };
  const handleDiscard = () => {
    if (isAnimating) return;

    console.log("📸 ScreenshotPreview - Starting closing animation");
    setIsAnimating(true);
    setAnimationPhase("closing");

    setTimeout(() => {
      // ✅ ADDED: Reset feedback state before closing
      if (!isEditMode) {
        setFeedback("");
        setHasAudioRecording(false);
        setAudioUrl(undefined);
        setAudioDownloadUrl(undefined);
        setAudioFilename(undefined);
        setAudioBlob(undefined);
      }

      onClose();
      setAnimationPhase("closed");
      setIsAnimating(false);
    }, 300);
  };
  const handleSubmit = async () => {
    const currentUserId = EnhancedTokenManager.getCurrentUserId();
    const workspaceId = EnhancedTokenManager.getWorkspaceIdForCurrentUser();
    const hasCompleteSetup = EnhancedTokenManager.hasCompleteSetup();

    console.log(
      `📸 ScreenshotPreview - ${isEditMode ? "Update" : "Submit"} clicked:`,
      {
        uniqueFeedbackId,
        hasContent,
        feedbackLength: feedback.trim().length,
        shapesCount: shapes.length,
        hasAudioRecording,
        currentUserId,
        workspaceId,
        hasCompleteSetup,
        isEditMode,
        previewDataUrl: preview?.dataUrl?.substring(0, 50),
      },
    );

    // ✅ NEW: Validate that feedback has actual text content
    if (!feedback.trim() || !hasActualTextContent(feedback)) {
      console.warn("📸 ScreenshotPreview - Cannot submit: No feedback content");
      setFeedbackError("Please add feedback before submitting the screenshot.");
      return;
    }

    // Clear error if validation passed
    setFeedbackError("");

    // ✅ VALIDATION: Check prerequisites
    if (!hasCompleteSetup) {
      console.error("❌ User setup incomplete");
      setSubmitError(
        "User authentication incomplete. Please refresh and try again.",
      );
      return;
    }

    if (!hasContent) {
      console.warn("⚠️ No content to submit");
      setSubmitError("Please add some content before submitting");
      return;
    }

    if (!currentUserId) {
      console.error("❌ No current user ID available");
      setSubmitError(
        "User authentication required. Please refresh and try again.",
      );
      return;
    }

    // ✅ CRITICAL FIX: Validate preview data exists
    if (!preview?.dataUrl) {
      console.error("❌ No preview data available");
      setSubmitError(
        "Screenshot data is missing. Please try taking the screenshot again.",
      );
      return;
    }

    setIsSubmitting(true);
    setSubmitError("");

    try {
      const startRect =
        previewContainerRef.current?.getBoundingClientRect() || null;

      console.log("📸 Preparing submission data:", {
        hasMessage: feedback.trim().length > 0,
        hasAnnotations: shapes.length > 0,
        hasAudio: hasAudioRecording,
        previewDataType: preview.dataUrl.substring(0, 20),
        isEditMode,
        isFromViewListFeedback,
        uniqueFeedbackId,
      });

      // Export annotated image
      let annotated: string;
      try {
        console.log("🎨 Starting exportAnnotatedImage...");
        annotated = await exportAnnotatedImage();
        console.log("✅ Export completed successfully:", {
          length: annotated.length,
          type: annotated.substring(0, 20),
        });
      } catch (exportError) {
        console.error("❌ Export failed:", exportError);
        const errorMsg =
          exportError instanceof Error ? exportError.message : "Unknown error";
        setSubmitError(
          `Failed to process screenshot: ${errorMsg}. ` +
            `This may happen with certain images. Please try taking the screenshot again.`,
        );
        setIsSubmitting(false);
        return;
      }

      // Validate exported image
      if (!annotated || !annotated.startsWith("data:image/")) {
        console.error("❌ Invalid annotated image data");
        setSubmitError(
          "Screenshot processing produced invalid data. Please try again.",
        );
        setIsSubmitting(false);
        return;
      }

      // ✅ NEW: Check if editing from ViewListFeedback - use reshare API
      if (isEditMode && uniqueFeedbackId && isFromViewListFeedback) {
        console.log(
          "✏️ Edit mode from ViewListFeedback - using UPLOAD_RESHARE_FEEDBACK API:",
          uniqueFeedbackId,
        );

        try {
          // Convert annotated image to blob
          const response = await fetch(annotated);
          if (!response.ok) {
            throw new Error("Failed to fetch image data");
          }

          const screenshotBlob = await response.blob();

          if (screenshotBlob.size === 0) {
            throw new Error("Image blob is empty");
          }

          console.log("🖼️ Screenshot blob prepared for reshare:", {
            size: screenshotBlob.size,
            sizeInMB: (screenshotBlob.size / (1024 * 1024)).toFixed(2),
            type: screenshotBlob.type,
          });

          // Call reshare API with all data in one request
          const reshareResult = await ReshareApiService.reshareFeedback(
            uniqueFeedbackId,
            feedback, // HTML formatted message
            screenshotBlob,
            hasAudioRecording ? audioBlob : undefined,
          );

          console.log("✅ Reshare API completed successfully:", reshareResult);
        } catch (reshareError) {
          console.error("❌ Reshare API failed:", reshareError);
          const errorMsg =
            reshareError instanceof Error
              ? reshareError.message
              : "Unknown error occurred";
          setSubmitError(
            `Failed to update screenshot: ${errorMsg}. Please try again.`,
          );
          setIsSubmitting(false);
          return;
        }
      }
      // ✅ EXISTING: Regular edit mode (not from ViewListFeedback) - use parallel uploads
      else if (isEditMode && uniqueFeedbackId) {
        console.log(
          "✏️ Regular edit mode - uploading updates to SAME feedback ID:",
          uniqueFeedbackId,
        );

        // ✅ PARALLEL UPLOADS - All uploads start simultaneously
        const uploadStartTime = Date.now();
        const uploadTasks: { name: string; promise: Promise<any> }[] = [];

        // 1. Message upload (if changed)
        if (feedback.trim().length > 0) {
          uploadTasks.push({
            name: "message",
            promise: (async () => {
              try {
                console.log(
                  "💬 [PARALLEL] Uploading updated message with HTML formatting...",
                );
                // ✅ CRITICAL: Send HTML content to preserve formatting
                await ScreenshotApiService.uploadMessage(
                  feedback, // ✅ This now contains HTML - no .trim() to preserve tags
                  uniqueFeedbackId,
                );
                console.log("✅ [PARALLEL] Message upload complete");
                return { type: "message", success: true };
              } catch (error) {
                console.error("❌ [PARALLEL] Message upload failed:", error);
                return { type: "message", success: false, error };
              }
            })(),
          });
        }

        // 2. Screenshot image upload
        if (annotated) {
          uploadTasks.push({
            name: "screenshot",
            promise: (async () => {
              try {
                console.log("🖼️ [PARALLEL] Uploading updated screenshot...");

                // Convert data URL to blob
                const response = await fetch(annotated);
                if (!response.ok) {
                  throw new Error("Failed to fetch image data");
                }

                const blob = await response.blob();

                if (blob.size === 0) {
                  throw new Error("Image blob is empty");
                }

                console.log("🖼️ [PARALLEL] Screenshot blob prepared:", {
                  size: blob.size,
                  sizeInMB: (blob.size / (1024 * 1024)).toFixed(2),
                  type: blob.type,
                });

                const pngBlob = new Blob([blob], { type: "image/png" });

                await ScreenshotApiService.uploadFile(
                  pngBlob,
                  `screenshot_${Date.now()}_updated.png`,
                  uniqueFeedbackId,
                );
                console.log("✅ [PARALLEL] Screenshot upload complete");
                return { type: "screenshot", success: true };
              } catch (error) {
                console.error("❌ [PARALLEL] Screenshot upload failed:", error);
                return { type: "screenshot", success: false, error };
              }
            })(),
          });
        }

        // 3. Audio upload (if present)
        if (hasAudioRecording && audioBlob) {
          uploadTasks.push({
            name: "audio",
            promise: (async () => {
              try {
                console.log("🎵 [PARALLEL] Uploading updated audio...");

                if (audioBlob.size === 0) {
                  throw new Error("Audio blob is empty");
                }

                await ScreenshotApiService.uploadVoice(
                  audioBlob,
                  audioFilename || `audio_${Date.now()}.mp3`,
                  uniqueFeedbackId,
                );
                console.log("✅ [PARALLEL] Audio upload complete");
                return { type: "audio", success: true };
              } catch (error) {
                console.error("❌ [PARALLEL] Audio upload failed:", error);
                return { type: "audio", success: false, error };
              }
            })(),
          });
        }

        // ✅ Wait for ALL uploads to complete in parallel
        console.log(`🚀 Waiting for ${uploadTasks.length} parallel uploads...`);
        const results = await Promise.all(uploadTasks.map((t) => t.promise));

        const uploadDuration = ((Date.now() - uploadStartTime) / 1000).toFixed(
          1,
        );
        console.log(`📊 All parallel uploads completed in ${uploadDuration}s`);

        // Check results
        const failedUploads = results.filter((r) => !r.success);
        const successfulUploads = results.filter((r) => r.success);

        console.log("📊 Upload results:", {
          total: results.length,
          successful: successfulUploads.length,
          failed: failedUploads.length,
          duration: `${uploadDuration}s`,
        });

        // Check if critical uploads failed (screenshot is critical)
        const screenshotResult = results.find((r) => r.type === "screenshot");
        if (screenshotResult && !screenshotResult.success) {
          console.error(
            "🚨 Critical upload (screenshot) failed:",
            screenshotResult.error,
          );
          setSubmitError(
            `Screenshot upload failed: ${
              screenshotResult.error?.message || "Unknown error"
            }`,
          );
          setIsSubmitting(false);
          return;
        }

        // Log any non-critical failures
        failedUploads.forEach((result) => {
          if (result.type !== "screenshot") {
            console.warn(
              `⚠️ Non-critical upload failed: ${result.type}`,
              result.error,
            );
          }
        });

        console.log(
          "✅ Edit mode: All critical uploads completed successfully",
        );
      }

      // ✅ Create submission data (same for both new and edit mode)
      const submissionId = `screenshot-${Date.now()}-${Math.random()
        .toString(36)
        .substr(2, 9)}`;

      const submission: ScreenshotSubmission = {
        id: submissionId,
        feedback: feedback,
        annotatedImage: annotated,
        originalImage: preview?.dataUrl || "",
        timestamp: Date.now(),
        hasAudio: hasAudioRecording,
        audioUrl: audioUrl,
        audioBlob: audioBlob,
        audioFilename: audioFilename,
        uniqueFeedbackId: uniqueFeedbackId,
        startRect: startRect,
      };

      console.log("📸 Final submission data prepared:", {
        submissionId,
        uniqueFeedbackId,
        annotatedImageLength: submission.annotatedImage.length,
        originalImageLength: submission.originalImage.length,
        hasStartRect: !!submission.startRect,
        isEditMode,
        isFromViewListFeedback,
      });

      // Pass submission to parent
      if (onSubmit) {
        console.log("📸 Passing submission to parent component");
        onSubmit(submission);
      }

      // Close preview with animation
      setAnimationPhase("closing");
      setIsAnimating(true);

      setTimeout(() => {
        setFeedback("");
        setShapes([]);
        setDraftShape(null);
        setTool("rect");
        setShowColorPalette(true);
        setShowWidthSelector(false);
        setBorderWidth(BORDER_WIDTHS[0]);
        setHasAudioRecording(false);
        setAudioUrl(undefined);
        setAudioDownloadUrl(undefined);
        setAudioFilename(undefined);
        setAudioBlob(undefined);
        setVoiceRecorderKey((prev) => prev + 1);

        onClose();
        setAnimationPhase("closed");
        setIsAnimating(false);
      }, 300);

      console.log(
        `✅ Screenshot ${
          isEditMode ? "update" : "submission"
        } completed successfully`,
      );
    } catch (error) {
      console.error("🚨 Screenshot submission failed:", error);

      const errorMsg =
        error instanceof Error ? error.message : "Unknown error occurred";
      setSubmitError(
        `Failed to ${
          isEditMode ? "update" : "submit"
        } screenshot: ${errorMsg}. Please try again.`,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRecordingComplete = (
    hasRecording: boolean,
    audioUrl?: string,
    downloadUrl?: string,
    filename?: string,
    audioBlob?: Blob,
  ) => {
    const currentUserId = EnhancedTokenManager.getCurrentUserId();

    console.log("📸 ScreenshotPreview - Recording complete:", {
      hasRecording,
      audioUrl: audioUrl ? "Present" : "None",
      downloadUrl: downloadUrl ? "Present" : "None",
      filename,
      blobSize: audioBlob?.size || 0,
      blobType: audioBlob?.type || "None",
      currentUserId,
    });

    setHasAudioRecording(hasRecording);
    setAudioUrl(audioUrl);
    setAudioDownloadUrl(downloadUrl);
    setAudioFilename(filename);
    setAudioBlob(audioBlob);

    if (hasRecording && audioBlob) {
      console.log("✅ Audio blob properly stored:", {
        size: audioBlob.size,
        type: audioBlob.type,
        filename: filename,
        currentUserId,
      });
    } else if (hasRecording && !audioBlob) {
      console.error(
        "❌ Audio recording marked as present but no blob provided!",
      );
    }
  };

  const TOOL_CONFIG: {
    id: Tool;
    activeIcon: React.FC<React.SVGProps<SVGSVGElement>>;
  }[] = [
    {
      id: "rect",
      activeIcon: RectangleActive,
    },
    {
      id: "selection",
      activeIcon: SelectionActive,
    },
    {
      id: "ellipse",
      activeIcon: EclipseActive,
    },
  ];

  const getCursorStyle = () => {
    // Priority 1: Active dragging/resizing
    if (dragState.mode !== "none") {
      if (dragState.mode === "move") return "cursor-move";
      if (dragState.mode === "resize-t" || dragState.mode === "resize-b")
        return "cursor-ns-resize";
      if (dragState.mode === "resize-l" || dragState.mode === "resize-r")
        return "cursor-ew-resize";
    }

    // Priority 2: Hovering over handles
    if (hoveredHandle !== "none") {
      if (hoveredHandle === "delete") return "cursor-pointer"; // ✅ NEW
      if (hoveredHandle === "move") return "cursor-move";
      if (hoveredHandle === "resize-t" || hoveredHandle === "resize-b")
        return "cursor-ns-resize";
      if (hoveredHandle === "resize-l" || hoveredHandle === "resize-r")
        return "cursor-ew-resize";
    }

    // Priority 3: Hovering over shape (move cursor)
    if (hoveredShape >= 0) {
      return "cursor-move";
    }

    // Priority 4: Default drawing cursor
    return "cursor-crosshair";
  };

  const canSubmit = () => {
    const hasValidFeedbackId =
      uniqueFeedbackId && String(uniqueFeedbackId).trim() !== "";
    const hasUserSetup = EnhancedTokenManager.hasCompleteSetup();

    return (
      shouldEnableButton &&
      hasValidFeedbackId &&
      hasUserSetup &&
      !isSubmitting &&
      !isAnimating
    );
  };

  if (!preview) return null;

  return createPortal(
    <>
      <style>{`
        @keyframes zoomIn {
          from {
            opacity: 0;
            transform: scale(0.1);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes zoomOut {
          from {
            opacity: 1;
            transform: scale(1);
          }
          to {
            opacity: 0;
            transform: scale(0.1);
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes fadeOut {
          from {
            opacity: 1;
          }
          to {
            opacity: 0;
          }
        }

        .zoom-overlay-enter {
          animation: fadeIn 0.5s ease-out forwards;
        }

        .zoom-overlay-exit {
          animation: fadeOut 0.3s ease-out forwards;
        }

        .zoom-content-enter {
          animation: zoomIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }

        .zoom-content-exit {
          animation: zoomOut 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }

        .animate-fadeIn {
  animation: fadeIn 0.2s ease-in;
}
      `}</style>

      <div
        className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 ${
          animationPhase === "opening" ? "zoom-overlay-enter" : ""
        } ${animationPhase === "closing" ? "zoom-overlay-exit" : ""}`}
      >
        <div
          ref={previewContainerRef}
          className={`bg-white rounded-[8px] max-w-[85vw] max-h-[95vh] flex flex-col overflow-hidden ${
            animationPhase === "opening" ? "zoom-content-enter" : ""
          } ${animationPhase === "closing" ? "zoom-content-exit" : ""}`}
        >
          <div className="flex flex-1 overflow-hidden gap-2 pl-1 pr-1.5 pt-1  pb-1">
            <div className="flex-1 rounded-[4px] flex items-center justify-center overflow-hidden">
              <div className="relative w-full h-full flex items-center justify-center ">
                <div
                  className="relative max-w-full max-h-full"
                  style={{ position: "relative" }}
                >
                  {isLoadingImage ? (
                    // Loading state
                    <div className="flex items-center justify-center w-full h-full bg-gray-100 rounded-[4px] min-h-[400px]">
                      <div className="flex flex-col items-center gap-3">
                        <svg
                          className="w-12 h-12 text-blue-500 animate-spin"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        <p
                          className="text-sm text-gray-600 font-medium"
                          style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                        >
                          Loading screenshot...
                        </p>
                        <p
                          className="text-xs text-gray-500"
                          style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                        >
                          Please wait
                        </p>
                      </div>
                    </div>
                  ) : preview?.dataUrl ? (
                    // Image loaded state
                    <img
                      ref={imgRef}
                      src={preview.dataUrl}
                      alt="Screenshot preview"
                      className="block max-w-full max-h-full object-contain rounded-[4px]"
                      style={{ display: "block", userSelect: "none" }}
                      onLoad={(e) => {
                        const target = e.currentTarget;
                        setNaturalSize({
                          w: target.naturalWidth,
                          h: target.naturalHeight,
                        });
                        setTimeout(() => {
                          resizeCanvas();
                        }, 50);
                        setIsLoadingImage(false);
                        console.log("✅ Image loaded successfully in preview");
                      }}
                      onError={(e) => {
                        console.error("❌ Failed to load image in preview");
                        setIsLoadingImage(false);
                      }}
                    />
                  ) : (
                    // Error state - no image available
                    <div className="flex items-center justify-center w-full h-full bg-gray-100 rounded-[4px] min-h-[400px]">
                      <div className="flex flex-col items-center gap-3">
                        <svg
                          className="w-12 h-12 text-red-500"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                          />
                        </svg>
                        <p
                          className="text-sm text-red-600 font-medium"
                          style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                        >
                          No image available
                        </p>
                        <p
                          className="text-xs text-gray-500"
                          style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                        >
                          Please close and try again
                        </p>
                      </div>
                    </div>
                  )}
                  <canvas
                    ref={canvasRef}
                    className={`absolute top-0 left-0 ${getCursorStyle()}`}
                    style={{
                      pointerEvents: "auto",
                      touchAction: "none",
                    }}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={() => {
                      if (isDrawing) {
                        handleMouseUp();
                      }
                      if (dragState.mode !== "none") {
                        handleMouseUp();
                      }
                    }}
                  />
                  {tool && (
                    <div
                      ref={paletteRef}
                      className="absolute bottom-4 right-4 rounded-[8px] z-10"
                    >
                      <div className="flex flex-col gap-2">
                        {showColorPalette ? (
                          <div
                            className="flex flex-col gap-1.5 bg-white rounded-[4px] shadow-lg border px-1 py-1 cursor-pointer"
                            onClick={() => setShowColorPalette(false)}
                          >
                            {COLORS.map((colorOption) => (
                              <button
                                key={colorOption}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setColor(colorOption);
                                  setShowColorPalette(false);
                                }}
                                className={`w-[18px] h-[18px] rounded-[2px] transition-all duration-150 ${
                                  color === colorOption
                                    ? "ring-2 ring-offset-1 ring-gray-700"
                                    : "hover:scale-105 hover:ring-1 hover:ring-offset-1 hover:ring-gray-400"
                                }`}
                                style={{
                                  backgroundColor: colorOption,
                                  fontFamily: DEFAULT_FONT_FAMILY,
                                }}
                                title={`Color: ${colorOption}`}
                              />
                            ))}
                          </div>
                        ) : (
                          <div
                            className="bg-white rounded-[4px] shadow-lg border px-1 py-1 cursor-pointer hover:bg-gray-50 transition"
                            style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                            onClick={() => {
                              setShowColorPalette(true);
                              setShowWidthSelector(false);
                            }}
                            title="Select color"
                          >
                            <div
                              className="w-[17px] h-[17px] rounded-[2px] border border-gray-300"
                              style={{ backgroundColor: color }}
                            />
                          </div>
                        )}

                        {showWidthSelector ? (
                          <div
                            className="flex flex-col gap-1.5 bg-white rounded-[4px] shadow-lg border p-1 cursor-pointer"
                            onClick={() => setShowWidthSelector(false)}
                          >
                            {BORDER_WIDTHS.map((width) => (
                              <button
                                key={width}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedBorderWidth(width);
                                  setShowWidthSelector(false);
                                }}
                                className={`w-[18px] h-4 rounded-[2px] transition-all duration-150 flex items-center justify-center ${
                                  selectedBorderWidth === width
                                    ? "bg-blue-50 ring-1 ring-blue-500 "
                                    : "hover:bg-gray-100 hover:ring-1 hover:ring-gray-300"
                                }`}
                                style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                                title={`Width: ${width}px`}
                              >
                                <div
                                  style={{
                                    width: "80%",
                                    height: `${width}px`,
                                    backgroundColor: color,
                                    borderRadius: "2px",
                                  }}
                                />
                              </button>
                            ))}
                          </div>
                        ) : (
                          <div
                            className="bg-white rounded-[4px] shadow-lg border px-1 py-2 cursor-pointer hover:bg-gray-50 transition"
                            onClick={() => {
                              setShowWidthSelector(true);
                              setShowColorPalette(false);
                            }}
                            style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                            title="Select border width"
                          >
                            <div
                              className="w-[18px] h-2 rounded-[2px] transition-all duration-150 flex items-center justify-center"
                              style={{ backgroundColor: "white" }}
                            >
                              <div
                                style={{
                                  width: "80%",
                                  height: `${selectedBorderWidth}px`,
                                  backgroundColor: color,
                                  borderRadius: "2px",
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="w-[22%] min-w-[280px] max-w-[440px] bg-white rounded-lg flex flex-col overflow-hidden">
              {submitError && (
                <div className="mx-4 mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                  <div className="flex">
                    <svg
                      className="w-5 h-5 text-red-400 mr-2 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <div
                      className="text-sm text-red-700"
                      style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                    >
                      {submitError}
                    </div>
                  </div>
                </div>
              )}

              <div
                className="pl-2 pb-2  flex flex-col"
                style={{ height: "50%" }}
              >
                {/* TextEditor - Adjusts height based on error */}
                <div
                  className="transition-all duration-300 overflow-hidden"
                  style={{
                    height: feedbackError ? "calc(100% - 32px)" : "100%",
                  }}
                >
                  <TextEditor
                    value={feedback}
                    onChange={(value) => {
                      setFeedback(value);
                      // Clear error when user starts typing actual content
                      if (feedbackError) {
                        // Check if there's actual text content (not just HTML tags)
                        const textOnly = value.replace(/<[^>]*>/g, "").trim();
                        if (textOnly.length > 0) {
                          setFeedbackError("");
                        }
                      }
                    }}
                    disabled={isSubmitting || isAnimating}
                    placeholder="Type your feedback here..."
                    onFocus={() => {
                      setIsTextEditorFocused(true);
                      setHasInteractedWithEditor(true);
                    }}
                    onBlur={() => {
                      setIsTextEditorFocused(false);
                    }}
                  />
                </div>

                {/* Error message display - Fixed space within the 50% container */}
                {feedbackError && (
                  <div className="pt-2 animate-fadeIn">
                    <div
                      className="text-red-500 text-sm font-medium"
                      style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                    >
                      {feedbackError}
                    </div>
                  </div>
                )}
              </div>

              <div className="pl-2 py-2">
                <VoiceNoteRecorder
                  key={`screenshot-voice-recorder-${voiceRecorderKey}`}
                  initialState={
                    // ✅ FIXED: Check the actual props, not local state
                    isEditMode && (existingAudioUrl || existingAudioBlob)
                      ? "recorded"
                      : "idle"
                  }
                  initialAudioUrl={
                    // ✅ FIXED: Pass if either URL or blob exists
                    isEditMode && (existingAudioUrl || existingAudioBlob)
                      ? existingAudioUrl
                      : undefined
                  }
                  onRecordingComplete={handleRecordingComplete}
                  disabled={isSubmitting || isAnimating}
                />
              </div>

              <div className="pl-2 py-2 flex-1 flex flex-col">
                <p
                  className="font-medium text-gray-700 mb-3 text-sm"
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                >
                  Highlight
                </p>
                <div className="flex items-start gap-3 mb-3 relative pb-2">
                  {TOOL_CONFIG.map(({ id, activeIcon: ActiveIcon }) => (
                    <div
                      key={id}
                      className="relative flex flex-col items-center"
                    >
                      <button
                        onClick={() => {
                          setPersistentTool(id);
                          setTool(id);
                          setShowColorPalette(false);
                          setShowWidthSelector(false);
                        }}
                        className={`
            flex items-center justify-center
            w-8 h-8
            transition-all duration-300
            relative
            disabled:opacity-50 disabled:cursor-not-allowed
            ${
              persistentTool === id
                ? "text-blue-600 -translate-y-1 scale-125"
                : "text-gray-600 hover:text-blue-600 hover:-translate-y-1 hover:scale-125"
            }
          `}
                        disabled={isSubmitting || isAnimating}
                      >
                        <ActiveIcon className="w-8 h-8" />
                      </button>
                      {persistentTool === id && (
                        <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-4 h-0.5 rounded-full transition-all duration-300" style={{ backgroundColor: '#53AEF4' }} />
                      )}
                    </div>
                  ))}
                </div>

                {/* ADD THIS NEW SECTION - Warning message in edit mode */}
                {isEditMode && (
                  <div className="flex items-start gap-2 px-3 py-2 bg-[#FFF9F0]  rounded-[4px]">
                    <AlertIcon className="w-4 h-4" />
                    <p
                      className="text-xs 2xl:text-[14px] text-[#1F1E1D] "
                      style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                    >
                      Earlier highlights can't be updated!
                    </p>
                  </div>
                )}
              </div>

              <div className="ml-2 border-t border-gray-200" />
              <div className="pl-2 pt-3 pb-1 flex gap-3">
                <Button
                  variant="cancel"
                  size="md"
                  onClick={handleDiscard}
                  className="flex-1"
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                  disabled={isSubmitting || isAnimating}
                >
                  Discard
                </Button>
                <Button
                  variant={shouldEnableButton ? "primary" : "secondary"}
                  size="md"
                  onClick={handleSubmit}
                  className="flex-1 relative"
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                  // ✅ FIX: Remove isSubmitting from disabled condition
                  // Only disable if there's no content or if animating
                  disabled={!shouldEnableButton || isAnimating}
                >
                  {isSubmitting ? (
                    <span
                      className="inline-block relative"
                      style={{ width: 40, height: 16 }}
                    >
                      <Lottie
                        animationData={DotLoader}
                        loop={true}
                        style={{
                          width: 120,
                          height: 48,
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                        }}
                      />
                    </span>
                  ) : isFromViewListFeedback ? (
                    "Save & notify"
                  ) : isEditMode ? (
                    "Update"
                  ) : (
                    "Add"
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default ScreenshotPreviewComponent;
